import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useNotification } from 'utils/hooks/useNotification';
import { userAtom } from '../../../utils/hooks/auth/userAtom';
import { ORCHESTRATION_STATUSES } from '../dashboardContants';
import { selectedRowsAtom } from '../atoms';
import { dashboardFilterAtom } from '../filterAtom';
import { stopJob } from './api';

const STOPPABLE_STATUSES: string[] = [
  ORCHESTRATION_STATUSES.PENDING,
  ORCHESTRATION_STATUSES.IN_PROGRESS,
];

export const useStopOrchestrations = () => {
  const selectedRows = useAtomValue(selectedRowsAtom);
  const user = useAtomValue(userAtom);
  const filters = useAtomValue(dashboardFilterAtom);
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const { mutate: stopOrchestrations, isPending } = useMutation({
    mutationFn: async () => {
      const stopPromises = selectedRows.map((row) => stopJob(row.jobId));
      await Promise.all(stopPromises);
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'An unknown error occurred';
      showErrorNotification(`Failed to stop orchestrations: ${message}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      showSuccessNotification('Orchestrations stopped successfully');
    },
  });

  const handleStopOrchestrations = () => {
    if (selectedRows.length === 0) {
      return;
    }
    stopOrchestrations();
  };

  const userCanStop =
    user?.user?.userRole?.permissions?.ORCHESTRATION_STOP === 'Allow';
  const canStopOrchestrations =
    selectedRows.length > 0 &&
    selectedRows.every(
      (row) =>
        row.status === ORCHESTRATION_STATUSES.IN_PROGRESS ||
        row.status === ORCHESTRATION_STATUSES.PENDING
    ) &&
    userCanStop;
  // 'ANY' means no status filter is applied, so every status (including
  // stoppable ones) can appear - the button must stay visible. Otherwise,
  // hide it only when none of the selected statuses are stoppable; if even
  // one selected status is stoppable, keep the button visible.
  const selectedStatusesIncludeStoppable =
    !filters?.jobStatuses ||
    filters.jobStatuses.includes('ANY') ||
    filters.jobStatuses.some((status) => STOPPABLE_STATUSES.includes(status));
  const hideStopButton = !selectedStatusesIncludeStoppable;

  const stopButtonTooltip = !canStopOrchestrations
    ? 'Select one or more rows to stop pending or actively running orchestration instances'
    : 'Stop selected orchestration instances';

  return {
    canStopOrchestrations,
    handleStopOrchestrations,
    hideStopButton,
    isPending,
    stopButtonTooltip,
  };
};
